import { useState, useEffect, useMemo, useRef } from 'react';

import { CustomNotification, dialogFunction } from 'common/components';
import { QaSpecHelpContentGridLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';

import { Typography } from 'antd';
import isEmpty from 'lodash/isEmpty';

import { useQaSpecGrid } from './useQaSpecGrid';

import { getAllRows } from 'pages/qa-spec/utils';
import { apiHandler } from 'utils/api';

import * as qaSpecServices from 'services/qaSpec';

import { useIntl } from 'react-intl';
import Messages from 'i18n/messages/qa-spec';
import { useContaminantCharacteristicsQuery } from 'pages/qa-spec/query';

import {
  getProductSpecRequiredFields,
  validateAllRows,
  validateContainmentCharacteristicGrid,
  validateRequiredFieldProductSpec,
} from 'pages/qa-spec/components/qa-prod-spec/utils';
import { useGetQaSpecRequiredFieldsInQaSpecFullView } from './useGetQaSpecRequiredFields';
import { addErrorsToRow } from 'pages/qa-spec/utils';

import { sleep } from 'utils/delay';
import { checkSectionRequired } from 'pages/qa-spec/utils';

import useCheckSnapshotForRetailer from './useCheckSnapshotForRetailer';

import {
  transformBooleanToStringQaSpecValue,
  transformStringToBooleanQaSpecValue,
} from 'pages/qa-spec/utils/transformQaBooleanValue';

export const useContaminant = ({ productId, isGridEditing }) => {
  const intl = useIntl();

  const gridInst = useRef();

  const [saveLoading, setSaveLoading] = useState(false);

  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId,
  });

  const isToggleRequiredFields = !isEmpty(requiredFields);

  const contaminantRequiredFields = getProductSpecRequiredFields({
    gridInst,
    requiredFields,
    requiredFieldPath:
      'qaSpecification.qaSpecProductSpecification.qaSpecProductSpecsContaminantCharacteristics'.toLowerCase(),
  });

  const resetGridCallback = () => {
    if (isToggleRequiredFields) {
      validateAllRows({
        gridInst,
        requiredFields: contaminantRequiredFields,
      });
    }
  };

  const isToggleSnapshot = useCheckSnapshotForRetailer();

  const {
    contaminantCharacteristicsData,
    handleRefetchContaminantCharacteristics,
    isLoading,
    isFetching,
  } = useContaminantCharacteristicsQuery({
    productId,
    enabled: !!productId && !isGridEditing,
  });

  const fetchDataLoading = isLoading && isFetching;

  const {
    addGridRow,
    deleteSelectedGridRow,
    selectGridRow,
    resetGridData,
    rowData,
    selectedRow,
  } = useQaSpecGrid({
    gridInst,
    isGridEditing,
    gridData: contaminantCharacteristicsData?.contaminantCharacteristics,
    resetGridCallback,
  });

  const isSectionRequired = checkSectionRequired(requiredFields, 'contaminant');

  const textEditorRenderer =
    (maxLength = 150) =>
    () => {
      return {
        component: 'TextEditor',
        popup: true,
        params: {
          maxLength: maxLength,
        },
      };
    };

  const columnDefs = useMemo(() => {
    const statusField = {
      minWidth: 50,
      field: 'error',
      displayName: ' ',
      cellRenderer: 'StatusRender',
      type: 'nonEditableColumn',
    };

    let cols = [
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Parameter',
        field: 'parameter',
        width: 120,
        flex: null,
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.contaminantCharacteristics.parameter',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Method',
        field: 'method',
        width: 200,
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.contaminantCharacteristics.method',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Value (Food and Non-Food products)',
        field: 'value',
        width: 350,
        flex: null,
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.contaminantCharacteristics.value',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Specification/Description/Target',
        field: 'specification',
        width: 300,
        flex: null,
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.contaminantCharacteristics.specification',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Tolerance',
        field: 'tolerance',
        width: 120,
        flex: null,
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.contaminantCharacteristics.tolerance',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Reject/Fail Criteria',
        field: 'rejectOrFailCriteria',
        width: 200,
        flex: null,
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.contaminantCharacteristics.rejectOrFailCriteria',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Ref. Standard',
        field: 'referenceStandard',
        width: 220,
        flex: null,
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.contaminantCharacteristics.referenceStandard',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Ref. Method',
        field: 'referenceMethod',
        width: 180,
        flex: null,
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.contaminantCharacteristics.referenceMethod',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Test Source',
        field: 'test',
        width: 160,
        flex: null,
        cellEditorSelector: () => {
          return {
            component: 'SelectionEditor',
            params: {
              values: [
                { value: 'Internal', displayName: 'Internal' },
                { value: 'External', displayName: 'External' },
              ],
              mapValue: (value) => {
                if (value === 'null' || value === undefined) {
                  return null;
                } else {
                  return value;
                }
              },
              popup: true,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.contaminantCharacteristics.test',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Frequency',
        field: 'frequency',
        width: 120,
        flex: null,
        cellEditorSelector: textEditorRenderer(),
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.contaminantCharacteristics.frequency',
        },
      },
      {
        allowFilter: true,
        allowSort: true,
        dataType: 'string',
        headerName: 'Release Criteria',
        field: 'releaseCriteria',
        width: 200,
        flex: null,
        cellEditorSelector: () => {
          return {
            component: 'SelectionEditor',
            params: {
              values: [
                { value: 'Yes', displayName: 'Yes' },
                { value: 'No', displayName: 'No' },
                { value: 'Not Applicable', displayName: 'Not Applicable' },
              ],
              mapValue: (value) => {
                if (value === 'null' || value === undefined) {
                  return null;
                } else {
                  return value;
                }
              },
              popup: true,
            },
          };
        },
        cellRenderer: isToggleSnapshot ? 'TextRenderSnapshot' : 'TextRender',
        headerComponent: QaSpecHelpContentGridLabel,
        headerComponentParams: {
          fieldPath:
            'qaSpecification.productSpecification.contaminantCharacteristics.releaseCriteria',
        },
      },
    ];

    cols = isToggleRequiredFields ? [statusField, ...cols] : cols;

    return cols.map((fieldItem) => {
      const DEFAULT_WIDTH = 150;

      let initColSetting = {
        type: 'editableColumn',
      };

      return {
        minWidth: DEFAULT_WIDTH,
        ...initColSetting,
        ...fieldItem,
      };
    });
  }, [isToggleRequiredFields, isToggleSnapshot]);

  const addNewRow = () => {
    let newRowData = {
      parameter: null,
      specification: null,
      tolerance: null,
      frequency: null,
      releaseCriteria: null,
      value: null,
      referenceStandard: null,
      referenceMethod: null,
      test: null,
    };

    if (isToggleRequiredFields) {
      const errorRequiredFields = contaminantRequiredFields?.map(
        (field) => field?.errorMessage
      );
      newRowData = addErrorsToRow(newRowData, errorRequiredFields);

      addGridRow(newRowData);
      return;
    }

    addGridRow(newRowData);
  };

  const saveData = async (toggleEditMode, analysisNotExecuted) => {
    gridInst.current.api.stopEditing();

    const GRID_VALIDATION_DELAY = 700;
    await sleep(GRID_VALIDATION_DELAY);

    // const isGridHaveError = validateContainmentCharacteristicGrid({
    //   grid: gridInst,
    // }).checkIfGridHaveError();

    // if (isGridHaveError) {
    //   CustomNotification.error(
    //     `Please resolve the Contaminant Characteristics grid's errors.`
    //   );
    //   return;
    // }
    const allRowData = getAllRows(gridInst);

    if (
      isToggleRequiredFields &&
      contaminantRequiredFields?.length > 0 &&
      allRowData?.length <= 0
    ) {
      CustomNotification.error('Please add at least one row data to save');
      return;
    }

    //* clear selected row
    selectGridRow(null);

    setSaveLoading(true);

    const params = {
      productId,
      contaminantCharacteristics: allRowData,
      analysisNotExecuted:
        transformStringToBooleanQaSpecValue(analysisNotExecuted),
    };

    const successMessage = intl.formatMessage(
      Messages.productSpecSaveContaminantSuccess
    );
    const errorMessage = intl.formatMessage(
      Messages.productSpecSaveContaminantError
    );

    apiHandler({
      service: qaSpecServices.saveQaSpecContaminantCharacteristic,
      params,
      successMessage,
      errorMessage,
      successCallback: saveSuccessCallback(toggleEditMode),
      onFinally: saveFinally,
    });
  };

  const saveSuccessCallback = (toggleEditMode) => () => {
    toggleEditMode();

    // refetch
    handleRefetchContaminantCharacteristics();
  };

  const saveFinally = () => {
    setSaveLoading(false);
  };

  const deleteData = () => {
    dialogFunction({
      type: 'warn',
      content: 'Are you sure you want to delete the selected item?',
      okText: 'OK',
      cancelText: 'Cancel',
      onOk: onDeleteData,
    });
  };

  const onDeleteData = () => {
    deleteSelectedGridRow();
  };

  const selectRow = () => {
    selectGridRow();
  };

  const cancelEdit = () => {
    resetGridData();
  };

  const handleValidateRequiredFieldForSupplier = ({ event }) => {
    contaminantRequiredFields?.forEach((item) => {
      if (item?.fieldName === event.column.colId)
        validateRequiredFieldProductSpec({
          gridInst,
          event,
          requiredFieldError: item.errorMessage,
        });
    });
  };

  const onCellEditingStopped = (event) => {
    if (isToggleRequiredFields) {
      handleValidateRequiredFieldForSupplier({
        event,
      });
    }
  };

  useEffect(() => {
    const handleValidateAllRowDataForSupplier = () => {
      if (rowData && rowData?.length > 0 && isToggleRequiredFields) {
        validateAllRows({
          gridInst,
          requiredFields: contaminantRequiredFields,
        });
      }
    };

    handleValidateAllRowDataForSupplier();
  }, [isToggleRequiredFields, requiredFields, JSON.stringify(rowData)]);

  const gridTitle = (
    <>
      {isToggleRequiredFields && isSectionRequired && (
        <>
          <Typography.Text type='danger'>*</Typography.Text>{' '}
        </>
      )}
      Contaminant Characteristics
    </>
  );

  return {
    gridInst,
    analysisNotExecuted: transformBooleanToStringQaSpecValue(
      contaminantCharacteristicsData?.analysisNotExecuted
    ),
    rowData,
    columnDefs,
    addNewRow,
    cancelEdit,
    saveData,
    selectedRow,
    selectRow,
    deleteData,
    loading: fetchDataLoading || saveLoading,
    onCellEditingStopped,
    isToggleRequiredFields,
    gridTitle,
  };
};
